/**
 * @architecture-header 1
 * @layer module-ui
 * @domain settings-preferences
 * @responsibility Render the SET-F03 Preferences pane from backend-authoritative preferences hooks.
 * @state-owner TanStack Query
 * @allowed-imports same-module hooks/components; shared translator model
 * @forbidden direct API client; hardcoded preferences data; display-name or avatar UI
 * @public-api PreferencesPage
 * @side-effects invokes authorized same-module commands and cache updates
 * @tests PreferencesSurface.test.tsx
 */

import { VispoLoading } from '../../../shared/components/WorkspaceState';
import { useTranslator } from '../../../shared/model/use-workspace';
import { usePreferences, useUpdatePreferences } from '../api/settings-preferences.queries';
import { PreferencesSurface } from './PreferencesSurface';

function errorCodeOf(error: unknown): string | null {
  return error && typeof error === 'object' && 'code' in error ? String(error.code) : null;
}

export function PreferencesPage({ mobile }: { mobile: boolean }) {
  const t = useTranslator();
  const preferencesQuery = usePreferences();
  const updatePreferences = useUpdatePreferences();

  if (preferencesQuery.isPending) {
    return <VispoLoading className="preferences-pane__loading" label={t('正在載入偏好設定')} />;
  }

  if (!preferencesQuery.data) {
    return (
      <div className="preferences-pane__error" role="alert">
        <span>{t('更新失敗，請重試')}</span>
        <button type="button" onClick={() => void preferencesQuery.refetch()}>
          {t('重新整理')}
        </button>
      </div>
    );
  }

  return (
    <PreferencesSurface
      errorCode={errorCodeOf(updatePreferences.error)}
      locale={preferencesQuery.data.locale}
      mobile={mobile}
      onRetry={() => {
        updatePreferences.reset();
        void preferencesQuery.refetch();
      }}
      onSelectLocale={(locale) => updatePreferences.mutate(locale)}
      saving={updatePreferences.isPending}
    />
  );
}
